/**
 * url-field — THE shared URL check + input for the event creation wizard.
 *
 * Every URL the wizard collects (provider join links, the post-registration
 * redirect, links inserted into terms copy) validates through `checkUrl` here,
 * so one definition of "a proper URL" holds across the flow:
 *   · http/https only (a `mailto:`/`javascript:`/`ftp:` link is rejected)
 *   · a real dotted host (or IPv4) — "teams", "foo bar", "https://" alone fail
 *   · a bare host typed without a scheme normalizes to https:// on blur rather
 *     than being rejected (what people actually paste)
 *
 * `expectHosts` adds a SOFT provider hint (a Zoom slot given a Teams link says
 * so) — informative only, never blocking: tenants do run vanity/self-hosted
 * meeting domains, and blocking on a host allow-list would lock them out.
 *
 * ui-config — presentational + pure validation; no data, no ops.
 */
import { useState, type CSSProperties } from "react";
import { Icon, T } from "@v0y4ge/jrni-ui";
import { useT } from "../../../lib/i18n";

export type UrlReason = "space" | "scheme" | "host";
export interface UrlCheck {
  state: "empty" | "valid" | "invalid";
  /** the value worth storing — scheme-normalized when we could parse it */
  normalized: string;
  reason?: UrlReason;
}

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const HOST_OK = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i;
const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/** Prepend https:// to a scheme-less entry; leave anything else untouched. */
export function normalizeUrl(raw: string): string {
  const v = raw.trim();
  if (!v) return "";
  if (HAS_SCHEME.test(v)) return v;
  if (v.startsWith("//")) return `https:${v}`;
  return `https://${v}`;
}

export function checkUrl(raw: string): UrlCheck {
  const v = raw.trim();
  if (!v) return { state: "empty", normalized: "" };
  if (/\s/.test(v)) return { state: "invalid", normalized: v, reason: "space" };
  if (HAS_SCHEME.test(v) && !/^https?:/i.test(v)) return { state: "invalid", normalized: v, reason: "scheme" };
  const candidate = normalizeUrl(v);
  let u: URL;
  try {
    u = new URL(candidate);
  } catch {
    return { state: "invalid", normalized: v, reason: "host" };
  }
  const host = u.hostname;
  if (!(HOST_OK.test(host) || IPV4.test(host))) return { state: "invalid", normalized: candidate, reason: "host" };
  return { state: "valid", normalized: candidate };
}

/** true when `url`'s host is (a subdomain of) one of `hosts`. */
export function hostMatches(url: string, hosts: string[]): boolean {
  try {
    const host = new URL(normalizeUrl(url)).hostname.toLowerCase();
    return hosts.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

export interface UrlFieldProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** example link shown in the error message */
  example?: string;
  /** soft provider-host hint */
  expectHosts?: string[];
  /** provider name used in the soft hint */
  expectName?: string;
  "aria-label"?: string;
  style?: CSSProperties;
}

export function UrlField({ id, value, onChange, placeholder, example, expectHosts, expectName, "aria-label": ariaLabel, style }: UrlFieldProps) {
  const t = useT();
  const [touched, setTouched] = useState(false);
  const res = checkUrl(value);
  const showError = touched && res.state === "invalid";
  const mismatch =
    !showError && res.state === "valid" && expectHosts && expectHosts.length > 0 && !hostMatches(value, expectHosts);

  const message = () => {
    if (res.reason === "space") return t("events.creationUrlField.noSpaces", "A link can't contain spaces — check for a stray space or a line break.");
    if (res.reason === "scheme") return t("events.creationUrlField.httpOnly", "Only http:// and https:// links can be used here.");
    return example
      ? `${t("events.creationUrlField.notAValidLink", "That isn't a valid link.")} ${t("events.creationUrlField.forExample", "For example:")} ${example}`
      : t("events.creationUrlField.notAValidLink", "That isn't a valid link.");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, ...style }}>
      <input
        id={id}
        value={value}
        type="url"
        inputMode="url"
        spellCheck={false}
        autoCapitalize="none"
        aria-label={ariaLabel}
        aria-invalid={showError || undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          setTouched(true);
          // Normalize a good, scheme-less entry once the user leaves the field —
          // never rewrite something we couldn't parse.
          if (res.state === "valid" && res.normalized !== value.trim()) onChange(res.normalized);
          else if (value !== value.trim()) onChange(value.trim());
        }}
        placeholder={placeholder}
        style={{
          width: "100%", height: 40, padding: "0 12px", borderRadius: 4, background: T.white,
          border: `1px solid ${showError ? T.red1 : T.n4}`, fontSize: 14, fontFamily: T.font, color: T.n1, outline: "none",
        }}
      />
      {showError ? (
        <div role="alert" style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, lineHeight: 1.45, color: T.red1, fontWeight: 500 }}>
          <span style={{ flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.warn size={12} /></span>
          <span>{message()}</span>
        </div>
      ) : mismatch ? (
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, lineHeight: 1.45, color: T.soft }}>
          <span style={{ flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.info size={12} /></span>
          <span>
            {t("events.creationUrlField.hostHint", "This doesn't look like a")}{" "}{expectName} {t("events.creationUrlField.hostHintTail", "link, but it will be saved as entered.")}
          </span>
        </div>
      ) : null}
    </div>
  );
}
